import { Button } from "@/components/ui/buttons/button";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSPAContext } from "@/providers/spa-provider";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { RouteNames } from "@/types/route-names";
import { hatch } from "@/services/breeding/hatch";
import { useAuth } from "@/providers/auth-provider";
import { Alert } from "@/components/alert/alert";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { HatchOutput, InventoryPokemonsOutput } from "types/web-app";
import { PokemonTalent } from "@/components/pokemon/pokemon-talent";
import { getPurityInfo, calculatePurityScore } from "@/utils/purity";
import { POKE_DATA } from "game/data/pokemon/data";
import { PokeNames } from "types/pokemon/poke-names";
import { useBackAction } from "@/hooks/use-back-action";

type EggType = InventoryPokemonsOutput["player_pokemons"][number];
type HatchedPokemonType = HatchOutput["pokemon"];

const HatchingSequence = ({
  egg,
  hatchedPokemon,
  onComplete,
}: {
  egg: EggType;
  hatchedPokemon: HatchedPokemonType;
  onComplete: () => void;
}) => {
  const [phase, setPhase] = useState<"shake" | "flash" | "reveal">("shake");
  const [showPokemon, setShowPokemon] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === "shake") {
      // Shake for 1.5 seconds then flash
      timeout = setTimeout(() => {
        setPhase("flash");
      }, 1500);
    } else if (phase === "flash") {
      // Flash for 500ms (white screen in) then reveal
      timeout = setTimeout(() => {
        setShowPokemon(true);
        setPhase("reveal");
      }, 500); // Wait a bit in the white screen
    } else if (phase === "reveal") {
      // Wait for reveal animation to finish before showing controls (handled by parent via onComplete if needed, or just stay here)
      timeout = setTimeout(() => {
        onComplete();
      }, 1500);
    }

    return () => clearTimeout(timeout);
  }, [phase, onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-xl">
      {/* Flash Overlay */}
      <div
        className={`absolute inset-0 bg-white pointer-events-none transition-opacity duration-1000 z-50 ${
          phase === "flash" ? "opacity-100" : "opacity-0"
        }`}
      />

      <div className="relative flex flex-col items-center justify-center w-full h-full">
        {/* Egg Animation */}
        {!showPokemon && (
          <div className="relative">
            <img
              src={`/assets/eggs/${egg.base_pokemon_name}.png`}
              alt="Egg"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "/assets/misc/poke-egg.png";
              }}
              className="w-32 h-auto rendering-pixelated"
              style={{
                animation:
                  phase === "shake"
                    ? "eggWiggle 0.5s ease-in-out infinite"
                    : "none",
                scale: phase === "shake" ? "1.2" : "1",
              }}
            />
            {phase === "shake" && (
              <StrokedText
                fontSize={24}
                strokeWidth={4}
                className="absolute -bottom-16 left-1/2 -translate-x-1/2 whitespace-nowrap animate-pulse"
              >
                It's hatching!
              </StrokedText>
            )}
          </div>
        )}

        {/* Pokemon Reveal */}
        {showPokemon && (
          <div className="relative flex flex-col items-center animate-fadeIn">
            <div className="relative">
              <AnimatedVFX
                id="reveal-sparkles"
                spriteUrl="/assets/spells/self/self_sparkles_006__50.png"
                position={{ x: 0, y: 0 }}
                style={{
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%) scale(4)",
                }}
                loops={1}
                speed={0.5}
              />
              <PokemonSprite
                pokemon={hatchedPokemon as any}
                style={{ scale: 6 }}
                className="z-20 animate-[fadeIn_1s_ease-out]"
              />
            </div>
            <div className="mt-12 flex flex-col items-center gap-2 animate-[fadeIn_1s_ease-out_0.5s_both]">
              <StrokedText
                fontSize={32}
                strokeWidth={6}
                className="text-pk-yellow-3"
              >
                Oh?
              </StrokedText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PokeReview = ({
  pokemon,
  onClose,
}: {
  pokemon: HatchedPokemonType;
  onClose: () => void;
}) => {
  const pokeData = POKE_DATA[pokemon.base_pokemon_name as PokeNames];
  const purityScore = calculatePurityScore(
    pokemon.talents ?? [],
    pokeData?.types ?? [],
  );
  const purityInfo = getPurityInfo(purityScore);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <StandardFrame
        className="w-full max-w-md overflow-visible shadow-cartoon-lg animate-fadeIn"
        variant="purple"
      >
        <div className="flex flex-col items-center p-6 gap-6 relative overflow-hidden">
          {/* Background Glow */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-pk-blue-5/20 to-transparent pointer-events-none transition-all duration-1000"
            style={{
              backgroundColor:
                purityInfo.vfxIntensity === "wow"
                  ? "rgba(251, 191, 36, 0.1)"
                  : "transparent",
            }}
          />

          <div className="relative z-10 flex flex-col items-center">
            <StrokedText
              fontSize={36}
              strokeWidth={8}
              className={`mb-2 ${
                purityInfo.vfxIntensity === "wow" ? "animate-bounce" : ""
              }`}
              fillColor={
                purityInfo.vfxIntensity === "wow" ? "#faaf1f" : "#FFFFFF"
              }
            >
              Congratulations!
            </StrokedText>
            <StrokedText
              fontSize={18}
              strokeWidth={4}
              className="text-pk-gray-4"
            >
              You hatched a {pokemon.base_pokemon_name}!
            </StrokedText>
          </div>

          <div className="relative z-10 p-8 bg-pk-beige-2/50 h-24 w-24 rounded-full border-4 border-pk-white/20 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
            {/* Rank-based VFX */}
            {(purityInfo.vfxIntensity === "high" ||
              purityInfo.vfxIntensity === "wow") && (
              <AnimatedVFX
                id="review-bg-vfx-aura"
                spriteUrl="/assets/spells/self/techaura__24.png"
                position={{ x: 0, y: 0 }}
                style={{
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%) scale(4)",
                  opacity: purityInfo.vfxIntensity === "wow" ? 0.6 : 0.3,
                  zIndex: 24,
                }}
                loops={Infinity}
                speed={0.3}
              />
            )}

            <AnimatedVFX
              id="review-bg-vfx"
              spriteUrl="/assets/spells/self/self_sparkles_006__50.png"
              position={{ x: 0, y: 0 }}
              style={{
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%) scale(5)",
                opacity:
                  purityInfo.vfxIntensity === "wow"
                    ? 1
                    : purityInfo.vfxIntensity === "high"
                    ? 0.85
                    : purityInfo.vfxIntensity === "medium"
                    ? 0.6
                    : purityInfo.vfxIntensity === "low"
                    ? 0.3
                    : 0,
                zIndex: 25,
              }}
              loops={Infinity}
              speed={purityInfo.vfxIntensity === "wow" ? 0.6 : 0.4}
            />
            <PokemonSprite
              pokemon={pokemon as any}
              style={{ scale: 6.5 }}
              className="z-30 drop-shadow-cartoon-lg"
            />
          </div>

          <div className="w-full bg-pk-purple-2 rounded-lg p-4 border-2 border-pk-purple-4 z-10">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-center gap-2 ">
                <span className="text-pk-gray-4 w-full text-center text-lg uppercase font-bold mb-1">
                  Talents
                </span>
                <div className="grid grid-cols-3 gap-2">
                  {pokemon.talents?.map((talent: string, index: number) => (
                    <PokemonTalent
                      key={`talent-${talent}-${index}`}
                      talent={talent.toString()}
                    />
                  )) || (
                    <span className="text-pk-gray-4 text-sm italic col-span-3">
                      No talents
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 relative">
                <span className="text-pk-gray-4 text-lg uppercase font-bold mb-1">
                  Purity
                </span>

                <div className="relative flex items-center justify-center">
                  {purityInfo.vfxIntensity === "wow" && (
                    <>
                      <div className="absolute inset-0 animate-ping rounded-full bg-pk-yellow-1/20 z-0" />
                      <span className="absolute -top-4 -left-4 animate-glitter text-2xl z-20">
                        ✨
                      </span>
                      <span className="absolute -bottom-4 -right-4 animate-glitter text-2xl z-20 [animation-delay:500ms]">
                        ✨
                      </span>
                      <span className="absolute top-0 -right-6 animate-glitter text-xl z-20 [animation-delay:1s]">
                        ✨
                      </span>
                    </>
                  )}

                  <StrokedText
                    fontSize={purityInfo.rank === "S+" ? 72 : 84}
                    strokeWidth={24}
                    className={`my-auto ${purityInfo.color} ${
                      purityInfo.hasShine ? "animate-pulse" : ""
                    } ${purityInfo.rank === "S+" ? "animate-rainbow" : ""}`}
                    fillColor="currentColor"
                  >
                    {purityInfo.rank}
                  </StrokedText>
                </div>
              </div>
            </div>
          </div>

          <Button
            variant={purityInfo.rank === "S+" ? "yellow" : "green"}
            onClick={onClose}
            className={`w-full mt-4 z-10 ${
              purityInfo.rank === "S+"
                ? "shadow-cartoon-yellow animate-bounce"
                : "shadow-cartoon-green"
            }`}
          >
            <span className="font-bold text-lg">
              {purityInfo.vfxIntensity === "wow" ? "WOOOOW!" : "Awesome!"}
            </span>
          </Button>
        </div>
      </StandardFrame>
    </div>
  );
};

export const PokemonEggInfoView = () => {
  const {
    selectedPokemons,
    setCurrentRoute,
    setSelectedPokemons,
    previousRoute,
  } = useSPAContext();
  const { getAccessToken } = useAuth();
  const [isHatching, setIsHatching] = useState(false);
  const [hatchedPokemon, setHatchedPokemon] =
    useState<HatchedPokemonType | null>(null);
  const [viewState, setViewState] = useState<"info" | "hatching" | "review">(
    "info",
  );

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute || RouteNames.INVENTORY_POKE);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const egg = selectedPokemons[0];

  // If no egg is selected, redirect back to the inventory
  if (!egg) {
    setCurrentRoute(RouteNames.INVENTORY_POKE);
    return null;
  }

  // Calculate hatch progress for eggs (1 day)
  const HATCH_TIME_MS = 24 * 60 * 60 * 1000; // 1 day in milliseconds

  const getHatchProgress = useMemo(() => {
    if (egg.state !== "EGG" || !egg.created_at)
      return { ratio: 0, remainingTime: "", isReady: false };

    const createdAt = new Date(egg.created_at).getTime();
    const now = Date.now();
    const elapsed = now - createdAt;

    if (elapsed >= HATCH_TIME_MS) {
      return { ratio: 1, remainingTime: "", isReady: true };
    }

    const ratio = elapsed / HATCH_TIME_MS;
    const remainingMs = HATCH_TIME_MS - elapsed;

    // Format remaining time
    const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor(
      (remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000),
    );

    let remainingTime = "";
    if (days > 0) {
      remainingTime = `${days}d ${hours}h`;
    } else if (hours > 0) {
      remainingTime = `${hours}h`;
    } else {
      remainingTime = "<1h";
    }

    return { ratio, remainingTime, isReady: false };
  }, [egg.created_at]);

  const { ratio, remainingTime, isReady } = getHatchProgress;

  const handleHatch = async () => {
    const accessToken = getAccessToken();
    if (!accessToken || !egg) return;

    setIsHatching(true);
    try {
      const result = await hatch(accessToken, { egg_id: egg.id });
      if (result.ok) {
        setHatchedPokemon(result.data.pokemon);
        setViewState("hatching");
      } else {
        Alert(result.message || "Failed to hatch egg");
        setIsHatching(false);
      }
    } catch (error) {
      console.error(error);
      Alert("An error occurred while hatching the egg");
      setIsHatching(false);
    }
  };

  const handleFinishReview = () => {
    setSelectedPokemons([]);
    setCurrentRoute(RouteNames.INVENTORY_POKE);
  };

  if (viewState === "hatching" && hatchedPokemon) {
    return (
      <HatchingSequence
        egg={egg}
        hatchedPokemon={hatchedPokemon}
        onComplete={() => setViewState("review")}
      />
    );
  }

  if (viewState === "review" && hatchedPokemon) {
    return <PokeReview pokemon={hatchedPokemon} onClose={handleFinishReview} />;
  }

  if (!egg)
    return (
      <div className="flex  h-full w-full items-center">
        <div className="border-b-2 items-center gap-2 justify-center flex flex-col shadow-cartoon border-pk-white p-4 w-full backdrop-blur-md bg-pk-black/60 rounded-xl ">
          <span className="text-2xl">No Egg Selected</span>
          <button className="bg-pk-white text-pk-black px-4 py-2 rounded-md">
            Back
          </button>
        </div>
      </div>
    );

  return (
    <div className="w-full max-w-full  overflow-x-hidden h-full p-1 bg-black/50 backdrop-blur-[5px]">
      <StandardFrame
        className="overflow-visible h-max max-h-full shadow-cartoon max-w-full overflow-x-hidden"
        variant="black"
      >
        <div className="flex flex-col h-max w-full pb-6 max-w-full overflow-x-hidden z-40 overflow-y-auto">
          <div className="w-full bg-pk-gray-2 px-3 pt-1 flex items-center justify-between">
            <StrokedText fontSize={40} strokeWidth={8}>
              Egg #{egg.display_id}
            </StrokedText>
          </div>

          <div className="grid grid-cols-5 items-stretch justify-between gap-4 relative p-4">
            {/* Left Side - Egg Info */}
            <div className="relative col-span-3 flex z-10 flex-col items-center justify-center gap-4">
              <div className="relative flex items-center justify-center h-32">
                <img
                  src={`/assets/eggs/${egg.base_pokemon_name}.png`}
                  alt="Pokemon Egg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/assets/misc/poke-egg.png";
                  }}
                  className="w-24 h-auto z-20 rendering-pixelated"
                />
              </div>

              <div className="flex flex-col w-full">
                <StrokedText fontSize={20} strokeWidth={8}>
                  Incubating...
                </StrokedText>

                <div className="flex flex-col items-start gap-0 w-full">
                  <StrokedText
                    fontSize={14}
                    strokeWidth={8}
                    height={1}
                    className="z-10"
                  >
                    Hatch Progress
                  </StrokedText>
                  <div className="w-full bg-pk-gray-2 rounded-lg border-4 border-pk-gray-4 h-6 flex items-center justify-start p-1">
                    <div
                      className="h-full bg-gradient-to-r from-pk-orange-2 to-pk-orange-4 rounded-md transition-all duration-1000"
                      style={{ width: `${ratio * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between w-full mt-1">
                    <span className="text-xs text-pk-gray-4">
                      {isReady
                        ? "Ready to hatch!"
                        : `Time remaining: ${remainingTime}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Egg Mystery Info */}
            <div className="relative col-span-2 flex z-10 flex-col items-center justify-center">
              <div className="flex flex-col w-full gap-4 bg-pk-gray-2 rounded-sm p-3 border-2 border-pk-gray-3">
                <div className="text-center w-full">
                  <StrokedText
                    fontSize={16}
                    strokeWidth={6}
                    height={1}
                    className="z-10"
                  >
                    ???
                  </StrokedText>
                  <p className="text-[10px] text-pk-gray-4 mt-0.5 leading-tight">
                    The contents of this egg are a mystery...
                  </p>
                </div>

                <div className="text-center w-full">
                  <StrokedText
                    fontSize={16}
                    strokeWidth={6}
                    height={1}
                    className="z-10"
                  >
                    ???
                  </StrokedText>
                  <p className="text-[10px] text-pk-gray-4 mt-0.5 leading-tight">
                    What Pokémon will hatch?
                  </p>
                </div>

                <div className="text-center w-full">
                  <StrokedText
                    fontSize={16}
                    strokeWidth={6}
                    height={1}
                    className="z-10"
                  >
                    ???
                  </StrokedText>
                  <p className="text-[10px] text-pk-gray-4 mt-0.5 leading-tight">
                    What will its nature be?
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Parents Section */}
          {egg.pokemon_parents && egg.pokemon_parents.length > 0 && (
            <div className="grid grid-cols-2 mt-2 gap-2 px-2">
              <div className="flex flex-col">
                <div className="bg-pk-garnet-2 w-max rounded-tr-lg text-pk-gray-4 font-bold px-2">
                  Parent A
                </div>
                <div className="bg-pk-garnet-2 rounded-sm rounded-tl-none p-2 flex flex-col items-center gap-2">
                  <PokemonSprite
                    pokemon={egg.pokemon_parents[0]!.parent}
                    style={{ scale: 2 }}
                    className="z-20"
                  />
                  <div className="text-center">
                    <div className="text-sm font-bold text-pk-gray-4">
                      #{egg.pokemon_parents[0]?.parent?.display_id}
                    </div>
                    <div className="text-xs text-pk-gray-5">
                      Lv. {egg.pokemon_parents[0]?.parent?.level}
                    </div>
                  </div>
                </div>
              </div>

              {egg.pokemon_parents.length > 1 && (
                <div className="flex flex-col">
                  <div className="bg-pk-garnet-2 w-max rounded-tr-lg text-pk-gray-4 font-bold px-2">
                    Parent B
                  </div>
                  <div className="bg-pk-garnet-2 rounded-sm rounded-tl-none p-2 flex flex-col items-center gap-2">
                    <PokemonSprite
                      pokemon={egg.pokemon_parents[1]!.parent}
                      style={{ scale: 2 }}
                      className="z-20"
                    />
                    <div className="text-center">
                      <div className="text-sm font-bold text-pk-gray-4">
                        #{egg.pokemon_parents[1]?.parent?.display_id}
                      </div>
                      <div className="text-xs text-pk-gray-5">
                        Lv. {egg.pokemon_parents[1]?.parent?.level}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {isReady && (
            <div className="flex justify-center w-full items-center gap-8 mt-8">
              <div className="relative flex items-center justify-center">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0">
                  <AnimatedVFX
                    id="hatch-btn-bg-vfx"
                    spriteUrl="/assets/spells/self/self_sparkles_006__50.png"
                    position={{ x: 0, y: 0 }}
                    style={{
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%) scale(2.5)",
                    }}
                    loops={Infinity}
                    speed={0.8}
                  />
                </div>
                <Button
                  variant="green"
                  onClick={handleHatch}
                  disabled={isHatching}
                  className="relative z-10 shadow-[0_0_20px_rgba(74,222,128,0.8)] hover:shadow-[0_0_30px_rgba(74,222,128,1)] transition-all duration-300 scale-110 animate-pulse border-pk-white"
                >
                  <span className="font-bold text-lg px-6 py-2 block text-pk-white drop-shadow-md">
                    {isHatching ? "Hatching..." : "✨ HATCH! ✨"}
                  </span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </StandardFrame>
    </div>
  );
};
